/**
 * Customer branding of this instance (DEV-182): the logo and text on the login
 * screen, and the suffix of the page title ("AssetRadar – <suffix>").
 *
 * Logos upload as soon as one is picked (PNG, JPEG or WebP, ≤ 1 MiB; the server
 * checks the content, SVG is refused). Text and title suffix are plain text and
 * saved together. What is saved goes straight into the branding context, so the
 * page title changes at once.
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Spinner from '../../components/Spinner';
import { deleteBrandingLogo, errorMessage, updateBrandingTexts, uploadBrandingLogo } from '../../lib/api';
import { useBranding } from '../../lib/branding';
import { pageTitle } from '../../lib/brandingModel';
import type { Branding, BrandingLogoVariant } from '../../lib/types';
import './branding.css';

function LogoField({ variant, branding, busy, onUpload, onRemove }: Readonly<{
  variant: BrandingLogoVariant;
  branding: Branding;
  busy: boolean;
  onUpload: (variant: BrandingLogoVariant, file: File) => void;
  onRemove: (variant: BrandingLogoVariant) => void;
}>): ReactNode {
  const { t } = useLingui();
  const logo = branding.logos[variant];
  const id = `branding-logo-${variant}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const label = variant === 'light' ? t`Logo für helle Darstellung` : t`Logo für dunkle Darstellung (optional)`;
  const hint = variant === 'light'
    ? t`Wird im hellen Modus gezeigt, im dunklen nur, wenn kein eigenes Logo dafür hinterlegt ist (dann auf heller Fläche).`
    : t`Für dunklen Hintergrund, z. B. mit heller Schrift. Fehlt es, erscheint das helle Logo auf einer hellen Fläche.`;

  function pick(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file !== undefined) onUpload(variant, file);
  }

  return (
    <div className="field">
      <span className="field-label" id={`${id}-label`}>{label}</span>
      <div className="branding-logo-row">
        <div className={`branding-logo-preview preview-${variant}`}>
          {logo !== null
            ? <img src={logo.url} alt={label} width={logo.width} height={logo.height} />
            : <span className="hint"><Trans>Kein Logo</Trans></span>}
        </div>
        <div className="branding-logo-actions">
          <input ref={inputRef} id={id} type="file" accept="image/png,image/jpeg,image/webp" hidden
            aria-labelledby={`${id}-label`} onChange={pick} />
          <button type="button" className="ghost" disabled={busy} aria-describedby={`${id}-label`}
            onClick={() => inputRef.current?.click()}>
            {logo !== null ? <Trans>Ersetzen…</Trans> : <Trans>Logo wählen…</Trans>}
          </button>
          {logo !== null ? (
            <button type="button" className="ghost" disabled={busy} onClick={() => onRemove(variant)}>
              <Trans>Entfernen</Trans>
            </button>
          ) : null}
        </div>
      </div>
      <span className="hint">{hint}</span>
    </div>
  );
}

export function BrandingCard(): ReactNode {
  const { t } = useLingui();
  const { branding, setBranding } = useBranding();
  const [text, setText] = useState('');
  const [suffix, setSuffix] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (branding === null) return;
    setText(branding.text);
    setSuffix(branding.title_suffix);
  }, [branding]);

  async function run(action: () => Promise<Branding>, done: string): Promise<void> {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      setBranding(await action());
      setSaved(done);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (branding === null) {
    return (
      <div className="card">
        <div className="card-head"><h2><Trans>Anmeldeseite und Seitentitel</Trans></h2></div>
        <div className="card-body"><Spinner label={t`Wird geladen…`} /></div>
      </div>
    );
  }

  const limits = branding.limits;
  const lines = text.split('\n').length;
  const textValid = text.length <= limits.text_max_chars && lines <= limits.text_max_lines;
  const suffixValid = suffix.length <= limits.title_suffix_max_chars && !suffix.includes('\n');
  const changed = text !== branding.text || suffix !== branding.title_suffix;
  const textLength = text.length;
  const textMax = limits.text_max_chars;
  const textMaxLines = limits.text_max_lines;
  const suffixMax = limits.title_suffix_max_chars;
  const preview = pageTitle(suffix);

  function saveTexts(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!textValid || !suffixValid || !changed || busy) return;
    void run(() => updateBrandingTexts(text, suffix), t`Text und Seitentitel gespeichert.`);
  }

  return (
    <div className="card branding-card">
      <div className="card-head">
        <h2><Trans>Anmeldeseite und Seitentitel</Trans></h2>
      </div>
      <div className="card-body">
        <p className="hint">
          <Trans>
            Logo und Text des Kunden erscheinen auf der Anmeldeseite über dem AssetRadar-Logo. Erlaubt sind PNG,
            JPEG und WebP bis 1 MiB, kein SVG.
          </Trans>
        </p>
        {error !== null ? <Banner kind="error">{error}</Banner> : null}
        {saved !== null ? <Banner kind="ok">{saved}</Banner> : null}

        <LogoField variant="light" branding={branding} busy={busy}
          onUpload={(variant, file) => void run(() => uploadBrandingLogo(variant, file), t`Logo gespeichert.`)}
          onRemove={(variant) => void run(() => deleteBrandingLogo(variant), t`Logo entfernt.`)} />
        <LogoField variant="dark" branding={branding} busy={busy}
          onUpload={(variant, file) => void run(() => uploadBrandingLogo(variant, file), t`Logo gespeichert.`)}
          onRemove={(variant) => void run(() => deleteBrandingLogo(variant), t`Logo entfernt.`)} />

        <form onSubmit={saveTexts}>
          <div className="field">
            <label htmlFor="branding-text"><Trans>Text unter dem Logo</Trans></label>
            <textarea id="branding-text" rows={3} value={text} disabled={busy}
              maxLength={limits.text_max_chars} aria-invalid={!textValid}
              onChange={(event) => { setSaved(null); setText(event.target.value); }} />
            <span className={textValid ? 'hint' : 'err'}>
              {t`${textLength} von ${textMax} Zeichen, höchstens ${textMaxLines} Zeilen. Reiner Text, ohne Formatierung.`}
            </span>
          </div>
          <div className="field">
            <label htmlFor="branding-title"><Trans>Zusatz im Seitentitel</Trans></label>
            <input id="branding-title" type="text" value={suffix} disabled={busy}
              maxLength={limits.title_suffix_max_chars} aria-invalid={!suffixValid}
              onChange={(event) => { setSaved(null); setSuffix(event.target.value); }} />
            <span className={suffixValid ? 'hint' : 'err'}>
              {t`Seitentitel: „${preview}“. Höchstens ${suffixMax} Zeichen; leer lassen für „AssetRadar“.`}
            </span>
          </div>
          <div className="ops-dt-actions">
            <span />
            <button type="submit" className="primary" disabled={!textValid || !suffixValid || !changed || busy}>
              {busy ? <Spinner variant="on-accent" label={t`Wird gespeichert…`} /> : t`Speichern`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BrandingCard;
